import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  deactivateChannelType,
  getApiErrorMessage,
  listChannelTypes,
} from '../../api/client';
import type { ChannelType } from '../../api/types';
import Badge from '../../components/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { canEditPlatformConfig, canWritePlatformConfig } from '../../utils/permissions';
import { CHANNEL_REPLY_MODE_LABELS } from '@21k/shared';
import { PLATFORM_CONFIG } from '../platform-config/paths';

export default function ChannelTypesPage() {
  const { user } = useAuth();
  const [types, setTypes] = useState<ChannelType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);

  const canCreate = user && canWritePlatformConfig(user);
  const canModify = user && canEditPlatformConfig(user);

  function loadTypes() {
    setLoading(true);
    listChannelTypes()
      .then(setTypes)
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadTypes();
  }, []);

  async function handleDeactivate(id: string, name: string) {
    if (!window.confirm(`Deactivate channel type "${name}"? Existing tickets keep this channel; new tickets cannot use it.`)) {
      return;
    }
    setDeactivatingId(id);
    setError('');
    try {
      const updated = await deactivateChannelType(id);
      setTypes((prev) => prev.map((t) => (t.id === id ? updated : t)));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setDeactivatingId(null);
    }
  }

  return (
    <>
      <div className="page-header" style={{ marginTop: 0 }}>
        <div>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Product surfaces where tickets originate — Parenthub, Garages, FLOWWW, LMS, and more.
          </p>
        </div>
        {canCreate && (
          <Link to={PLATFORM_CONFIG.channelTypesNew} className="btn btn-primary">
            Add channel type
          </Link>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        {loading ? (
          <div className="loading">Loading channel types…</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Reply mode</th>
                  <th>Configs</th>
                  <th>Tickets</th>
                  <th>Status</th>
                  {canModify && <th />}
                </tr>
              </thead>
              <tbody>
                {types.map((t) => (
                  <tr key={t.id}>
                    <td>
                      {canModify ? (
                        <Link to={PLATFORM_CONFIG.channelType(t.id)}>{t.code}</Link>
                      ) : (
                        <code>{t.code}</code>
                      )}
                      {t.isSystem && (
                        <>
                          {' '}
                          <Badge label="SYSTEM" className="badge-system" />
                        </>
                      )}
                    </td>
                    <td>{t.name}</td>
                    <td style={{ fontSize: '0.85rem' }}>
                      {CHANNEL_REPLY_MODE_LABELS[t.replyMode]}
                    </td>
                    <td>{t._count?.channelConfigs ?? '—'}</td>
                    <td>{t._count?.tickets ?? '—'}</td>
                    <td>
                      <Badge
                        label={t.isActive ? 'ACTIVE' : 'INACTIVE'}
                        className={t.isActive ? 'badge-success' : 'badge-neutral'}
                      />
                    </td>
                    {canModify && (
                      <td style={{ textAlign: 'right' }}>
                        {t.isActive && (
                          <button
                            type="button"
                            className="btn btn-danger-ghost"
                            disabled={deactivatingId === t.id}
                            onClick={() => void handleDeactivate(t.id, t.name)}
                          >
                            {deactivatingId === t.id ? '…' : 'Deactivate'}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
